import React from 'react';
import { UserCheck, Clock, ArrowRight, Home, UserPlus } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { AssistantCheck, AssistantProposal, AssistantParamOption } from '../api/assistant.api';
import { ROUTE_PATHS } from '../../../app/router/route-paths';
import styles from './assistant-interactive.module.css';

interface ConflictResolverProps {
  check: AssistantCheck;
  proposal: AssistantProposal;
  stepIndex: number;
  onUpdateParam: (stepIndex: number, key: string, value: unknown) => void;
  onRevalidate: () => void;
}

export const ConflictResolver: React.FC<ConflictResolverProps> = ({
  check,
  proposal,
  stepIndex,
  onUpdateParam,
  onRevalidate
}) => {
  const currentStep = proposal.plan?.steps?.[stepIndex] ?? proposal.display_plan?.steps?.[stepIndex];

  // 1. Коллизии тренера (занят другим слотом или вне рабочих часов)
  const isTrainerConflict =
    check.code === 'trainer_overlap' ||
    check.code === 'trainer_hours' ||
    (check.code.includes('trainer') && check.status === 'blocked');

  // 2. Коллизии зала / пространства
  const isSpaceConflict =
    check.code === 'space_overlap' ||
    check.code === 'space_hours' ||
    (check.code.includes('space') && check.status === 'blocked');

  // 3. Клиент не найден
  const isClientNotFound = check.code === 'client' && check.status === 'blocked';

  if (!isTrainerConflict && !isSpaceConflict && !isClientNotFound) {
    return null;
  }

  // --- Ветка: Клиент не найден ---
  if (isClientNotFound) {
    return (
      <div className={styles.conflictResolutionBlock} data-testid="assistant-client-resolver">
        <div className={styles.conflictResolutionTitle}>Клиент не найден:</div>
        <div className="flex flex-wrap gap-1.5 items-center">
          <Link
            to={ROUTE_PATHS.clients}
            className={styles.conflictOptionButton}
          >
            <UserPlus size={12} /> Создать клиента в базе
          </Link>
        </div>
      </div>
    );
  }

  // --- Ветка: Зал занят / не подходит ---
  if (isSpaceConflict) {
    const spacesList: AssistantParamOption[] = proposal.options?.space_id ?? [];
    const currentSpaceId = currentStep?.params?.space_id;
    const availableSpaces = spacesList.filter((s) => s.id !== currentSpaceId);

    const handleSelectSpace = (spaceId: number) => {
      onUpdateParam(stepIndex, 'space_id', spaceId);
      setTimeout(() => onRevalidate(), 50);
    };

    return (
      <div className={styles.conflictResolutionBlock} data-testid="assistant-space-resolver">
        <div className={styles.conflictResolutionTitle}>Быстрое решение для зала:</div>
        {availableSpaces.length > 0 ? (
          <div className="flex flex-col gap-1.5">
            <span className="text-xs text-[hsl(var(--ui-muted-foreground))] flex items-center gap-1">
              <Home size={12} /> Выбрать другой свободный зал:
            </span>
            <div className="flex flex-wrap gap-1.5">
              {availableSpaces.slice(0, 4).map((space) => (
                <button
                  key={space.id}
                  type="button"
                  className={styles.conflictOptionButton}
                  onClick={() => handleSelectSpace(space.id)}
                >
                  {space.name}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <span className="text-xs text-[hsl(var(--ui-muted-foreground))]">
            Нет доступных альтернативных залов в этом филиале.
          </span>
        )}
      </div>
    );
  }

  // --- Ветка: Тренер занят ---
  const trainersList: AssistantParamOption[] = proposal.options?.trainer_user_id ?? [];
  const currentTrainerId = currentStep?.params?.trainer_user_id;

  // Исключаем текущего конфликтующего тренера
  const availableTrainers = trainersList.filter((t) => t.id !== currentTrainerId);

  // Быстрые варианты сдвига времени на +1 час или +2 часа, если время указано
  const startTime = currentStep?.params?.start_time ? String(currentStep.params.start_time).slice(0, 5) : null;
  const slotDate = currentStep?.params?.slot_date ? String(currentStep.params.slot_date) : null;

  const shiftedTimes: string[] = [];
  if (startTime && startTime.includes(':')) {
    const [h, m] = startTime.split(':').map(Number);
    if (!isNaN(h) && !isNaN(m)) {
      if (h + 1 < 23) shiftedTimes.push(`${String(h + 1).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
      if (h + 2 < 23) shiftedTimes.push(`${String(h + 2).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }

  const handleSelectTrainer = (trainerId: number) => {
    onUpdateParam(stepIndex, 'trainer_user_id', trainerId);
    setTimeout(() => {
      onRevalidate();
    }, 50);
  };

  const handleShiftTime = (newStartTime: string) => {
    onUpdateParam(stepIndex, 'start_time', `${newStartTime}:00`);
    // Автоматически сдвигаем end_time, если была исходная длительность
    const origStart = currentStep?.params?.start_time ? String(currentStep.params.start_time).slice(0, 5) : null;
    const origEnd = currentStep?.params?.end_time ? String(currentStep.params.end_time).slice(0, 5) : null;
    if (origStart && origEnd) {
      const [sh, sm] = origStart.split(':').map(Number);
      const [eh, em] = origEnd.split(':').map(Number);
      const durationMin = (eh * 60 + em) - (sh * 60 + sm);
      if (durationMin > 0) {
        const [nsh, nsm] = newStartTime.split(':').map(Number);
        const totalNewEnd = nsh * 60 + nsm + durationMin;
        const newEndH = Math.floor(totalNewEnd / 60);
        const newEndM = totalNewEnd % 60;
        if (newEndH < 24) {
          onUpdateParam(stepIndex, 'end_time', `${String(newEndH).padStart(2, '0')}:${String(newEndM).padStart(2, '0')}:00`);
        }
      }
    }
    setTimeout(() => {
      onRevalidate();
    }, 50);
  };

  return (
    <div className={styles.conflictResolutionBlock} data-testid="assistant-conflict-resolver">
      <div className={styles.conflictResolutionTitle}>Быстрое решение коллизии:</div>

      {availableTrainers.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <span className="text-xs text-[hsl(var(--ui-muted-foreground))] flex items-center gap-1">
            <UserCheck size={12} /> Назначить другого тренера:
          </span>
          <div className="flex flex-wrap gap-1.5">
            {availableTrainers.slice(0, 4).map((trainer) => (
              <button
                key={trainer.id}
                type="button"
                className={styles.conflictOptionButton}
                onClick={() => handleSelectTrainer(trainer.id)}
              >
                {trainer.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {shiftedTimes.length > 0 && (
        <div className="flex flex-col gap-1.5 mt-1">
          <span className="text-xs text-[hsl(var(--ui-muted-foreground))] flex items-center gap-1">
            <Clock size={12} /> Сдвинуть время {slotDate ? `на ${slotDate}` : ''}:
          </span>
          <div className="flex flex-wrap gap-1.5">
            {shiftedTimes.map((newT) => (
              <button
                key={newT}
                type="button"
                className={styles.conflictOptionButton}
                onClick={() => handleShiftTime(newT)}
              >
                <ArrowRight size={12} /> на {newT}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
